/**
 * Small DOM helpers.
 *
 * Everything here builds nodes and sets `textContent`. Document titles, signer
 * names and email addresses all come from other people; none of it is ever
 * interpolated into HTML.
 */

type EventName = keyof HTMLElementEventMap;

export interface ElOptions {
  class?: string;
  text?: string;
  attrs?: Record<string, string>;
  on?: { [K in EventName]?: (event: HTMLElementEventMap[K]) => void };
  children?: (Node | string | null | false)[];
}

/** Create an element. Text is always set as text, never parsed as markup. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.class !== undefined) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.attrs) {
    for (const [name, value] of Object.entries(options.attrs)) node.setAttribute(name, value);
  }
  if (options.on) {
    for (const [name, handler] of Object.entries(options.on)) {
      node.addEventListener(name, handler as EventListener);
    }
  }
  if (options.children) {
    for (const child of options.children) {
      if (child === null || child === false) continue;
      node.append(child);
    }
  }
  return node;
}

/** Replace everything inside a host element. */
export function replaceChildren(host: HTMLElement, ...nodes: (Node | string)[]): void {
  host.replaceChildren(...nodes);
}

/** Announce a message to screen readers through the shared live region. */
export function announce(message: string): void {
  const region = document.getElementById('live-region');
  if (region) region.textContent = message;
}

/** Set the tab title, always suffixed with the product name. */
export function setDocumentTitle(prefix: string | null): void {
  document.title = prefix ? `${prefix} - Better Call Sign` : 'Better Call Sign';
}

/* -------------------------------------------------------------------------- */
/* Buttons                                                                    */
/* -------------------------------------------------------------------------- */

const IDLE_LABEL = Symbol('idle-label');

interface BusyButton extends HTMLButtonElement {
  [IDLE_LABEL]?: string;
}

/**
 * Put a button into a busy state: disabled, labelled, and announced.
 *
 * Returning it to idle restores the original label, so callers never have to
 * remember what it said.
 */
export function setBusy(button: HTMLButtonElement, busy: boolean, busyLabel?: string): void {
  const target = button as BusyButton;
  if (busy) {
    if (target[IDLE_LABEL] === undefined) target[IDLE_LABEL] = button.textContent ?? '';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (busyLabel !== undefined) button.textContent = busyLabel;
    return;
  }
  button.disabled = false;
  button.removeAttribute('aria-busy');
  const idle = target[IDLE_LABEL];
  if (idle !== undefined) {
    button.textContent = idle;
    delete target[IDLE_LABEL];
  }
}

/**
 * Wrap an async click handler so a button can never be double-submitted and a
 * rejection can never go unhandled.
 */
export function onClickAsync(
  button: HTMLButtonElement,
  busyLabel: string,
  handler: () => Promise<void>,
): void {
  let running = false;
  button.addEventListener('click', () => {
    if (running || button.disabled) return;
    running = true;
    setBusy(button, true, busyLabel);
    void handler().finally(() => {
      running = false;
      // The handler may have unmounted the view; only revive a live button.
      if (button.isConnected) setBusy(button, false);
    });
  });
}

/* -------------------------------------------------------------------------- */
/* Messages                                                                   */
/* -------------------------------------------------------------------------- */

export type NoticeTone = 'error' | 'success' | 'info';

/** A dismissable inline message. Errors are announced assertively. */
export function notice(tone: NoticeTone, message: string): HTMLElement {
  return el('p', {
    class: `notice notice-${tone}`,
    text: message,
    attrs: { role: tone === 'error' ? 'alert' : 'status' },
  });
}

/** A block that owns one slot for a message, so messages replace each other. */
export interface MessageSlot {
  node: HTMLElement;
  show(tone: NoticeTone, message: string): void;
  clear(): void;
}

export function messageSlot(): MessageSlot {
  const node = el('div', { class: 'message-slot' });
  return {
    node,
    show(tone, message) {
      node.replaceChildren(notice(tone, message));
      announce(message);
    },
    clear() {
      node.replaceChildren();
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Formatting                                                                 */
/* -------------------------------------------------------------------------- */

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/** Format an ISO timestamp, or return null when it is missing or unparseable. */
export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return DATE_FORMAT.format(parsed);
}

/** Join names into readable prose: "Ana", "Ana and Bo", "Ana, Bo and Cy". */
export function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  const head = names.slice(0, -1).join(', ');
  const tail = names[names.length - 1] ?? '';
  return `${head} and ${tail}`;
}

/** Loose email sanity check - the real validation is the email arriving. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
